import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, MenuItem, FormControl, InputLabel, ListItem, ListItemText, ListItemIcon, Typography, Grid, Box, List } from '@mui/material';
import { FaChevronDown } from 'react-icons/fa';
import { useQuery } from '@apollo/client';
import Flag from 'react-world-flags';
import { useTranslation } from 'react-i18next'; 
import { GET_ALL_COUNTRIES } from '../../graphql/queries/countryStateCity';

const RegionSelector = ({ onSelect }) => {
    const [open, setOpen] = useState(false);
    const [language, setLanguage] = useState('');
    const [country, setCountry] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [countryId, setCountryId] = useState('');
    const { data, loading, error } = useQuery(GET_ALL_COUNTRIES);

    useEffect(() => {
        const defaults = {
            selectedLanguage: 'English',
            selectedCountry: 'United States',
            selectedCountryCode: 'US',
            currentCountryId: 228
        };

        const savedValues = Object.keys(defaults).reduce((acc, key) => {
            const savedValue = localStorage.getItem(key);
            if (savedValue) {
                acc[key] = savedValue;
            } else {
                localStorage.setItem(key, defaults[key]);
                acc[key] = defaults[key];
            }
            return acc;
        }, {});

        setLanguage(savedValues.selectedLanguage);
        setCountry(savedValues.selectedCountry);
        setCountryCode(savedValues.selectedCountryCode);
        setCountryId(savedValues.currentCountryId);
    }, []);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const { t, i18n } = useTranslation();

    const handleLanguageChange = (event) => {
        setLanguage(event.target.value);
        localStorage.setItem('selectedLanguage', event.target.value);
        i18n.changeLanguage(event.target.value);
    };

    const handleCountrySelect = (selectedCountry, selectedCountryCode, selectedCountryId) => {
        setCountry(selectedCountry);
        setCountryCode(selectedCountryCode);
        setCountryId(selectedCountryId);
        localStorage.setItem('selectedCountry', selectedCountry);
        localStorage.setItem('selectedCountryCode', selectedCountryCode);
        localStorage.setItem('currentCountryId', selectedCountryId);
        if (typeof onSelect === 'function') {
            onSelect({ language, country: selectedCountry, countryCode: selectedCountryCode, countryId: selectedCountryId });
        }
        // hard reload the page
        window.location.reload();
        handleClose();
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error :</p>;

    const countries = data?.getAllCountries || [];

    // Group countries by region.name
    const groupedByRegion = countries.reduce((acc, country) => {
        const regionName = country.region.name;
        if (!acc[regionName]) {
            acc[regionName] = [];
        }
        acc[regionName].push(country);
        return acc;
    }, {});

    return (
        <>
            <Button onClick={handleOpen} variant="text" color="inherit">
                {countryCode && <Flag code={countryCode} height="16" style={{ marginRight: '0.1em' }} />}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {country && countryCode}
                    <FaChevronDown
                        style={{
                            marginLeft: '2px',
                            color: (theme) => theme.palette.mode === 'dark'
                                ? theme.palette.primary.contrastText
                                : theme.palette.primary.main,
                        }}
                    />
                </Box>
            </Button>
            <Modal open={open} onClose={handleClose}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    width: '400px',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                }}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="language-select-label">{t('language')}</InputLabel>
                        <Select
                            labelId="language-select-label"
                            id="language-select"
                            value={language}
                            onChange={handleLanguageChange}
                        >
                            <MenuItem value="en">English</MenuItem>
                            {/* <MenuItem value="am">አማርኛ</MenuItem> // TODO
                            <MenuItem value="fr">French</MenuItem> */}
                        </Select>
                    </FormControl>

                    {Object.keys(groupedByRegion).map((regionName) => (
                        <div key={regionName}>
                            <Typography variant="h6" style={{ marginTop: '20px' }}>{regionName}</Typography>
                            <List>
                                {groupedByRegion[regionName].map((country) => (
                                    <ListItem
                                        key={country.id}
                                        onClick={() => handleCountrySelect(country.name, country.code.toUpperCase(), country.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <ListItemIcon>
                                            <Flag code={country.code} height="24" />
                                        </ListItemIcon>
                                        <ListItemText primary={country.name} />
                                    </ListItem>
                                ))}
                            </List>
                        </div>
                    ))}
                </div>
            </Modal>
        </>
    );
};

export default RegionSelector;
